import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize, firstValueFrom } from 'rxjs';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../core/services/auth.service';
import Swal from 'sweetalert2';

function contrasenasCoinciden(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password')?.value;
  const confirmar = control.get('confirmarPassword')?.value;

  if (!password || !confirmar) {
    return null;
  }

  return password === confirmar ? null : { contrasenasDiferentes: true };
}

@Component({
  selector: 'app-restablecer-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './restablecer-password.html',
  styleUrl: './restablecer-password.scss',
})
export class RestablecerPassword {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  token = '';

  mostrarPassword = false;
  mostrarConfirmacion = false;

  cargando = false;
  exito = false;
  error = '';

  readonly form = this.fb.nonNullable.group(
    {
      password: ['', [Validators.required, Validators.minLength(12), Validators.maxLength(128)]],
      confirmarPassword: ['', [Validators.required]],
    },
    {
      validators: contrasenasCoinciden,
    },
  );

  constructor() {
    this.token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';

    if (!this.token) {
      this.error = 'El enlace no es válido o no contiene un token de recuperación.';
    }
  }

  async guardarPassword(): Promise<void> {
    if (this.form.hasError('contrasenasDiferentes')) {
      Swal.fire({
        icon: 'error',
        text: 'Las contraseñas no coinciden',
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      });
    }
    if (!this.token || this.form.invalid || this.cargando) {
      this.form.markAllAsTouched();
      return;
    }

    this.cargando = true;
    this.error = '';

    const password = this.form.controls.password.value;

    try {
      const res = await firstValueFrom(this.authService.cambiarPassword(password, this.token));

      await Swal.fire({
        icon: 'success',
        title: 'Cambio de contraseña exitoso',
        showConfirmButton: false,
        timer: 1000,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      });

      this.router.navigate(['/login']);
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        text: error['error']?.message ?? 'No se pudo cambiar la contraseña',
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          title: 'mi-swal-title',
          htmlContainer: 'mi-swal-text',
        },
      });
    }
  }

  volverLogin(): void {
    this.router.navigate(['/login']);
  }

  solicitarOtroEnlace(): void {
    this.router.navigate(['/recuperar-password']);
  }
}
